import { useMemo, useState } from 'react'
import Select from 'react-select'
import Container from '@/components/container'
import Link from '@/components/link-component'
import PageWrapper from '@/components/page-wrapper'
import ImageContentful from '@/components/ui/image-contentful'
import SectionCta from '@/components/section/cta'
import FilterHeader from '@/components/ui/filters-header'
import TagButton from '@/components/ui/tag-button'
import { dateFormat, slugify } from '@/utils/strings'
import useGetArticles from '@/utils/useGetArticles'
import { usePage } from '@inertiajs/react'
import { useTranslations } from '@/utils/locale'

export default function Newsfeed(props: any) {
  const { t } = useTranslations()
  const { numVisible, setNumVisible, numPerPage, selectedTag, setSelectedTag } = useGetArticles(usePage().url)
  const [reactSelectedTag, setReactSelectedTag] = useState([])
  const newsItems = props.newsfeed
  const filteredNewsItems = useMemo(
    () =>
      newsItems.filter(
        (item: any) =>
          !selectedTag ||
          selectedTag === 'all' ||
          item.fields.tags?.includes(selectedTag) ||
          item.fields.tags?.includes(selectedTag.replaceAll('-', ' '))
      ),
    [selectedTag]
  )
  const newsItemTags = [
    ...new Set([...newsItems.map((item) => item.fields.tags?.map((tag) => tag.toLowerCase())).flat(1)]),
  ]
    .filter((tag) => tag !== undefined)
    .sort((a, b) => {
      if (a < b) return -1
      if (a > b) return 1
      return 0
    })

  const tags = ['all', ...newsItemTags].map((tag) => ({
    label: tag === 'msp' || tag === 'cli' || tag === 'aws' || tag === '2fa' ? tag.toUpperCase() : tag,
    value: tag !== 'all' ? slugify(tag) : '',
  }))

  return (
    <PageWrapper
      headerNav={props.headerNav}
      footerNav={props.footerNav}
      headerBanners={props.headerBanners}
      newsletterForm={props.newsletterForm}
    >
      <section className="bg-indigoBlue dark:bg-transparent text-white py-12 flex items-center content-center justify-center">
        <h1 className="h3 px-5 lg:px-0 mb-0">{t('The Bitwarden Newsfeed')}</h1>
      </section>
      <Container>
        {props.sectionCta && (
          <SectionCta className="hidden md:block pt-12 px-0!" {...(props.sectionCta.fields as any)} />
        )}

        {selectedTag && (
          <h2 className="hidden lg:block mt-12">
            {t('Tag')} &ldquo;{selectedTag.replaceAll('-', ' ')}&rdquo;
          </h2>
        )}
        <div className="lg:hidden py-10">
          <FilterHeader title={t('Tags')} icon="&#xe95e;" />
          <Select
            name="tags"
            value={reactSelectedTag}
            options={tags}
            onChange={(selected) => {
              setNumVisible(numPerPage)
              setSelectedTag(selected.value)
              setReactSelectedTag(selected as [])
            }}
            isClearable={false}
            className="dark:text-gray-950 capitalize"
          />
        </div>

        <section className="lg:flex lg:py-14">
          <div className="lg:flex-1 lg:pr-8">
            <div className="flex flex-col gap-12">
              {filteredNewsItems.slice(0, numVisible).map((item, index) => {
                const {
                  fields: { url, title, authors, publicationDate, publicationName, tags, logoImage, description, id },
                } = item
                return (
                  <Link to={url} key={`newsfeed-${index}`}>
                    <article className="text-center flex flex-col gap-4 p-10 shadow-md rounded-4xl bg-secondary-50 hover:bg-white transition-all duration-200 hover:scale-[1.02] hover:shadow-lg xl:flex-row xl:text-left dark:bg-secondary-900 dark:hover:bg-secondary-900 dark:shadow-2xl">
                      <div className="w-32 flex-none">
                        {logoImage?.fields && <ImageContentful {...logoImage.fields} alt="image" />}
                      </div>
                      <div className="grow">
                        <h3 className="text-2xl mb-2">{title}</h3>
                        <div className="flex gap-3 mb-4 justify-center xl:justify-start">
                          <div className="bg-primaryBlue text-white rounded-md px-2 inline-block">
                            {publicationName}
                          </div>
                          {authors && <span className="text-secondary-500">{authors.join(', ')}</span>}
                        </div>
                        <p>{description}</p>
                      </div>

                      <div className="flex-none text-secondary-500 text-xs font-medium xl:w-52 xl:text-right">
                        <div className="flex flex-col justify-between gap-3 h-full">
                          <div>{dateFormat(publicationDate, 'en-US')}</div>
                          {tags && <div>{tags.join(', ')}</div>}
                        </div>
                      </div>
                    </article>
                  </Link>
                )
              })}
              <div>
                {numVisible < filteredNewsItems.length && (
                  <div className="text-center mt-2">
                    <button
                      type="button"
                      className="font-bold bg-secondary-50! py-2 px-4 rounded-md hover:bg-secondary-100"
                      onClick={() => setNumVisible(numVisible + numPerPage)}
                    >
                      {t('Load More')}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="hidden lg:block mx-auto w-[300px] mt-10 xl:mt-0">
            <FilterHeader title={t('Tags')} icon="&#xe95e;" />
            <div className="flex flex-col gap-1">
              {tags.map((tag) => (
                <>
                  <TagButton
                    selectedTag={selectedTag}
                    tag={tag}
                    handleOnClick={() => {
                      setNumVisible(numPerPage)
                      setSelectedTag(tag.value)
                      setReactSelectedTag(tag as [])
                    }}
                  />
                </>
              ))}
            </div>
          </div>
        </section>
      </Container>
    </PageWrapper>
  )
}
